import { Head, router, useForm } from "@inertiajs/react"
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  BookOpen,
  Users,
  GripVertical,
  Eye,
  EyeOff,
  Clock,
  CheckCircle2,
  XCircle,
  FileText,
} from "lucide-react"
import { useEffect, useState } from "react"
import { AdminLayout } from "../components/AdminLayout"
import { AssignGroupDialog } from "../components/AssignGroupDialog"
import { CourseCoverImage } from "@/components/CourseCoverImage"
import { Button } from "@/components/ui/button"
import { FileUploadField } from "@/components/ui/file-upload-field"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { CourseOverview } from "@/components/course/CourseOverview"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogClose,
} from "@/components/ui/responsive-dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function StatusBadge({ status }) {
  if (status === "published") {
    return (
      <Badge className="bg-green-500 text-white hover:bg-green-500">
        Published
      </Badge>
    )
  }
  return <Badge variant="secondary">Draft</Badge>
}

function VisibilityBadge({ visibility }) {
  if (visibility === "coaches_only") {
    return (
      <Badge variant="outline" className="border-amber-500 text-amber-600">
        Coaches Only
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="border-blue-500 text-blue-500">
      All Members
    </Badge>
  )
}

function formatReviewDate(dateString) {
  if (!dateString) return ""
  return new Date(dateString).toLocaleDateString("en-CA")
}

// ---------------------------------------------------------------------------
// Edit Course Dialog
// ---------------------------------------------------------------------------

function EditCourseDialog({ open, onOpenChange, course, categories = [] }) {
  const form = useForm({
    title: course?.title || "",
    description: course?.description || "",
    course_category_id: course?.category?.id ? String(course.category.id) : "",
    visibility: course?.visibility || "all_members",
    status: course?.status || "draft",
    cover_image: null,
    _purge_cover_image: false,
  })
  const { transform } = form

  // Never send cover_image unless it is a real upload. Inertia's forceFormData
  // serializer coerces a null value to "", which Active Storage's
  // has_one_attached writer reads as an explicit DeleteOne — so saving any
  // other field silently purged the cover (task #524). Omitting the key is the
  // frontend defense; `reject_blank_attachments` in the controller is the
  // backstop that holds even against a stale bundle mid-deploy.
  useEffect(() => {
    transform((data) => {
      if (data.cover_image instanceof File) return data
      const { cover_image, ...rest } = data
      return rest
    })
  }, [transform])

  const handleSubmit = (e) => {
    e.preventDefault()
    // onSuccess is the only close path. A validation failure (e.g. flipping to
    // published with no published lesson, or a rejected cover image) comes back
    // as an Inertia error payload — the dialog stays open and `form.errors`
    // renders inline instead of closing with no visible reason (task #338.1).
    form.patch(`/app/admin/courses/${course.id}`, {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
    })
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              Edit Course
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              Update the course details.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            {/* Base / general error (e.g. anything the model attaches to :base) */}
            {form.errors.base && (
              <p
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {form.errors.base}
              </p>
            )}

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="edit-course-title">
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit-course-title"
                placeholder="Course title"
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.title}
                onChange={(e) => form.setData("title", e.target.value)}
                required
              />
              {form.errors.title && (
                <p className="text-sm text-red-600">{form.errors.title}</p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="edit-course-description">Description</Label>
              <Textarea
                id="edit-course-description"
                placeholder="Optional description..."
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 min-h-[80px]"
                value={form.data.description}
                onChange={(e) => form.setData("description", e.target.value)}
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label>
                Category <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.data.course_category_id}
                onValueChange={(v) => form.setData("course_category_id", v)}
                required
              >
                <SelectTrigger className="bg-white border-zinc-300 text-zinc-900">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={String(cat.id)}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.errors.course_category_id && (
                <p className="text-sm text-red-600">
                  {form.errors.course_category_id}
                </p>
              )}
            </div>

            {/* Visibility */}
            <div className="space-y-2">
              <Label>Visibility</Label>
              <Select
                value={form.data.visibility}
                onValueChange={(v) => form.setData("visibility", v)}
              >
                <SelectTrigger className="bg-white border-zinc-300 text-zinc-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_members">All Members</SelectItem>
                  <SelectItem value="coaches_only">Coaches Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={form.data.status}
                onValueChange={(v) => form.setData("status", v)}
              >
                <SelectTrigger className="bg-white border-zinc-300 text-zinc-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
              {/* Publishing without a published lesson is rejected on :status by
                  Course#must_have_published_lesson_to_publish — surface it here. */}
              {form.errors.status && (
                <p className="text-sm text-red-600">{form.errors.status}</p>
              )}
            </div>

            {/* Cover Image */}
            <div className="space-y-2">
              <Label htmlFor="edit-course-cover-image">Cover Image</Label>
              <FileUploadField
                id="edit-course-cover-image"
                accept="image/webp,image/png,image/jpeg"
                value={form.data.cover_image}
                onChange={(f) => form.setData("cover_image", f)}
                previewSrc={course?.coverImageUrl}
                existingFileUrl={course?.coverImageUrl}
                pendingRemoval={form.data._purge_cover_image}
                onRequestRemoveExisting={() =>
                  form.setData("_purge_cover_image", true)
                }
                onUndoRemoveExisting={() =>
                  form.setData("_purge_cover_image", false)
                }
                helpText="WebP, PNG, or JPEG. Max 5 MB."
              />
              {form.errors.cover_image && (
                <p className="text-sm text-red-600">
                  {form.errors.cover_image}
                </p>
              )}
            </div>
          </div>

          <ResponsiveDialogFooter>
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </ResponsiveDialogClose>
            <Button
              type="submit"
              variant="gradient"
              disabled={form.processing}
            >
              {form.processing ? "Saving..." : "Save Changes"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

// ---------------------------------------------------------------------------
// Lesson Form Dialog (Create / Edit)
// ---------------------------------------------------------------------------

function LessonFormDialog({ open, onOpenChange, courseId, lesson = null, nextPosition = 1 }) {
  const isEditing = !!lesson
  const form = useForm({
    title: lesson?.title || "",
    position: lesson?.position ?? (isEditing ? "" : nextPosition),
    status: lesson?.status || "draft",
    coach_only: lesson?.coachOnly || false,
  })

  // When opening fresh for adding, prefill with the latest computed
  // next position so it tracks lessons added in earlier sessions.
  useEffect(() => {
    if (open && !isEditing) {
      form.setData("position", String(nextPosition))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditing, nextPosition])

  const handleSubmit = (e) => {
    e.preventDefault()

    const options = {
      preserveScroll: true,
      onSuccess: () => {
        onOpenChange(false)
        if (!isEditing) {
          form.reset()
        }
      },
    }

    if (isEditing) {
      form.patch(`/app/admin/courses/${courseId}/lessons/${lesson.id}`, options)
    } else {
      form.post(`/app/admin/courses/${courseId}/lessons`, options)
    }
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              {isEditing ? "Edit Lesson" : "Add Lesson"}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {isEditing
                ? "Update lesson details."
                : "Add a new lesson to this course."}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="lesson-title">
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="lesson-title"
                placeholder="Lesson title"
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.title}
                onChange={(e) => form.setData("title", e.target.value)}
                required
              />
              {form.errors.title && (
                <p className="text-sm text-red-600">{form.errors.title}</p>
              )}
            </div>

            {/* Position */}
            <div className="space-y-2">
              <Label htmlFor="lesson-position">Position</Label>
              <Input
                id="lesson-position"
                type="number"
                min="0"
                placeholder="e.g. 1"
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.position}
                onChange={(e) => form.setData("position", e.target.value)}
              />
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={form.data.status}
                onValueChange={(v) => form.setData("status", v)}
              >
                <SelectTrigger className="bg-white border-zinc-300 text-zinc-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Coach Only */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Coach Only</Label>
                <p className="text-xs text-zinc-400">
                  Only visible to coaches when enabled
                </p>
              </div>
              <Switch
                checked={form.data.coach_only}
                onCheckedChange={(checked) =>
                  form.setData("coach_only", checked)
                }
              />
            </div>
          </div>

          <ResponsiveDialogFooter>
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </ResponsiveDialogClose>
            <Button
              type="submit"
              variant="gradient"
              disabled={form.processing}
            >
              {form.processing
                ? isEditing
                  ? "Saving..."
                  : "Creating..."
                : isEditing
                  ? "Save Changes"
                  : "Add Lesson"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function CourseShow({
  currentUser,
  course,
  categories,
  availableGroups,
  pendingCount,
  pendingPublicReviewCount,
  fromTrack = null,
  memberPreview = null,
}) {
  const [viewMode, setViewMode] = useState("edit")
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [addLessonOpen, setAddLessonOpen] = useState(false)
  const [deleteLesson, setDeleteLesson] = useState(null)
  const [assignGroupOpen, setAssignGroupOpen] = useState(false)
  const [removeGroup, setRemoveGroup] = useState(null)
  const [removeFile, setRemoveFile] = useState(null)
  const [approving, setApproving] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectionReason, setRejectionReason] = useState("")
  const [rejectionError, setRejectionError] = useState("")
  const [rejecting, setRejecting] = useState(false)

  const handleApprove = () => {
    if (approving) return
    setApproving(true)
    router.post(
      `/app/admin/courses/${course.id}/approve_public`,
      {},
      {
        preserveScroll: true,
        onFinish: () => setApproving(false),
      }
    )
  }

  const handleRejectSubmit = (e) => {
    e.preventDefault()
    const trimmed = rejectionReason.trim()
    if (!trimmed) {
      setRejectionError("A rejection reason is required.")
      return
    }
    setRejecting(true)
    router.post(
      `/app/admin/courses/${course.id}/reject_public`,
      { rejection_reason: trimmed },
      {
        preserveScroll: true,
        // Only close + clear the dialog on success. If the request errors
        // (e.g., a concurrent admin already approved, validation failed),
        // keep the dialog open so the admin doesn't lose their typed reason.
        onSuccess: () => {
          setRejectOpen(false)
          setRejectionReason("")
          setRejectionError("")
        },
        onError: () => {
          setRejectionError("Could not reject the course. Please try again.")
        },
        onFinish: () => setRejecting(false),
      }
    )
  }

  const handleDeleteCourse = () => {
    router.delete(`/app/admin/courses/${course.id}`, {
      onSuccess: () => setDeleteOpen(false),
    })
  }

  const handleDeleteLesson = () => {
    if (!deleteLesson) return
    router.delete(
      `/app/admin/courses/${course.id}/lessons/${deleteLesson.id}`,
      {
        preserveScroll: true,
        onSuccess: () => setDeleteLesson(null),
      }
    )
  }

  const handleReorder = (lesson, newPosition) => {
    router.patch(
      `/app/admin/courses/${course.id}/lessons/${lesson.id}/reorder`,
      { position: newPosition },
      { preserveScroll: true }
    )
  }

  const handleRemoveGroup = () => {
    if (!removeGroup) return
    router.delete(
      `/app/admin/courses/${course.id}/group_assignments/${removeGroup.groupCourseId}`,
      {
        preserveScroll: true,
        onSuccess: () => setRemoveGroup(null),
      }
    )
  }

  const handleRemoveFile = () => {
    if (!removeFile) return
    router.delete(`/app/admin/courses/${course.id}/files/${removeFile.id}`, {
      preserveScroll: true,
      onSuccess: () => setRemoveFile(null),
    })
  }

  // Removing the last file of a course with no member-visible lessons hides
  // the course from members (course_has_viewer_visible_content?).
  const removingLastContent =
    course.courseFiles?.length === 1 &&
    !course.lessons?.some((l) => l.status === "published" && !l.coachOnly)

  return (
    <AdminLayout currentUser={currentUser} pendingCount={pendingCount}>
      <Head title={`${course.title} - Kingdom Factor Admin`} />

      <div className="space-y-6">
        {/* ---------------------------------------------------------------- */}
        {/* Section 1 — Course Details                                       */}
        {/* ---------------------------------------------------------------- */}
        <div>
          <button
            onClick={() =>
              router.visit(fromTrack ? `/app/admin/learning_tracks/${fromTrack.id}` : "/app/admin/courses")
            }
            className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700 mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {fromTrack ? `Back to ${fromTrack.title}` : "Back to Courses"}
          </button>

          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap mb-2">
                <h1 className="text-2xl font-bold text-zinc-900">
                  {course.title}
                </h1>
                <StatusBadge status={course.status} />
                <VisibilityBadge visibility={course.visibility} />
                {course.category && (
                  <Badge variant="outline">{course.category.name}</Badge>
                )}
              </div>
              {course.description && (
                <p className="text-zinc-500 mt-1">{course.description}</p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setEditOpen(true)}
                title="Edit course"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setDeleteOpen(true)}
                title="Delete course"
                className="text-red-500 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Cover image preview — path-only URLs; on failure shows fallback */}
          <div className="mt-4">
            <CourseCoverImage
              url={course.coverImageUrl}
              alt={`Cover for ${course.title}`}
              wrapperClassName="inline-flex h-40 min-w-[12rem] max-w-lg rounded-lg border border-zinc-200 overflow-hidden"
              imgClassName="h-full w-full object-cover"
              fallbackIconClassName="h-10 w-10 text-zinc-300"
            />
          </div>
        </div>

        {/* Coach catalog: an admin shares a course or takes it back out.
            Pending review keeps its own approve / reject card below. */}
        {course.publicStatus !== "pending_review" && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-200 bg-white px-4 py-3">
            {course.publicStatus === "public" ? (
              <Eye className="h-4 w-4 text-green-600 shrink-0" />
            ) : (
              <EyeOff className="h-4 w-4 text-zinc-400 shrink-0" />
            )}
            <div className="flex-1 min-w-0 text-sm">
              <span className="font-medium text-zinc-900">
                Coach catalog: {course.publicStatus === "public" ? "Public" : "Private"}
              </span>
              <span className="text-zinc-500">
                {course.publicStatus === "public"
                  ? " — every coach can find this course and assign it to their groups."
                  : " — coaches cannot find it. It is used only where an admin assigns it, such as a track."}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="min-h-[36px]"
              onClick={() =>
                router.patch(
                  `/app/admin/courses/${course.id}/catalog`,
                  { shared: course.publicStatus !== "public", track: fromTrack?.id },
                  { preserveScroll: true }
                )
              }
            >
              {course.publicStatus === "public" ? "Make private" : "Make public"}
            </Button>
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Public Review status                                             */}
        {/* ---------------------------------------------------------------- */}
        {course.publicStatus === "pending_review" && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="space-y-2 flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-md bg-amber-100">
                      <Clock className="h-4 w-4 text-amber-700" />
                    </div>
                    <h2 className="text-lg font-semibold text-amber-900">
                      Pending public review
                    </h2>
                  </div>
                  <p className="text-sm text-amber-800">
                    Submitted by{" "}
                    <span className="font-medium">
                      {course.createdBy?.name || "—"}
                    </span>
                    {course.submittedAt && (
                      <> on {formatReviewDate(course.submittedAt)}</>
                    )}
                  </p>
                  <p className="text-sm text-amber-700">
                    Approving makes this course available to other coaches in
                    the public library.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                  <Button
                    variant="gradient"
                    onClick={handleApprove}
                    disabled={approving || rejecting}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    {approving ? "Approving..." : "Approve"}
                  </Button>
                  <Button
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    onClick={() => {
                      setRejectionReason("")
                      setRejectionError("")
                      setRejectOpen(true)
                    }}
                    disabled={approving || rejecting}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject with reason
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {course.publicStatus === "public" && course.reviewedBy && (
          <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
            <span>
              Public — approved by{" "}
              <span className="font-medium">{course.reviewedBy.name}</span>
              {course.reviewedAt && (
                <> on {formatReviewDate(course.reviewedAt)}</>
              )}
              .
            </span>
          </div>
        )}

        {course.publicStatus === "rejected" && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
              <span className="font-medium">
                Rejected
                {course.reviewedBy && <> by {course.reviewedBy.name}</>}
                {course.reviewedAt && (
                  <> on {formatReviewDate(course.reviewedAt)}</>
                )}
              </span>
            </div>
            {course.rejectionReason && (
              <p className="ml-6">Reason: {course.rejectionReason}</p>
            )}
          </div>
        )}

        {/* Edit / Preview (plan 2026-09-23-001): Preview renders the member
            course page itself, through the shared CourseOverview. */}
        <div className="flex items-center justify-end">
          <div className="inline-flex rounded-md border border-zinc-200 bg-zinc-50 p-0.5 text-sm">
            {[
              { value: "edit", label: "Edit" },
              { value: "preview", label: "Preview as member" },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setViewMode(opt.value)}
                className={`px-3 py-1.5 rounded transition-colors ${
                  viewMode === opt.value
                    ? "bg-white text-zinc-900 shadow-sm font-medium"
                    : "text-zinc-500 hover:text-zinc-700"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {viewMode === "preview" && memberPreview ? (
          <Card className="bg-white border-zinc-200">
            <CardContent className="p-6">
              <p className="mb-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                This is what a member sees. Draft and coach-only lessons are hidden. Open a lesson to preview it.
              </p>
              <div className="space-y-6 max-w-3xl mx-auto">
                <CourseOverview
                  course={{ ...course, files: memberPreview.files }}
                  lessons={memberPreview.lessons}
                  progress={{ completed: 0, total: memberPreview.lessons.length }}
                  lessonHref={(lesson) => `/app/admin/courses/${course.id}/lessons/${lesson.id}?view=preview`}
                />
              </div>
            </CardContent>
          </Card>
        ) : (
        <>
        {/* ---------------------------------------------------------------- */}
        {/* Section 2 — Lessons                                              */}
        {/* ---------------------------------------------------------------- */}
        <Card className="bg-white border-zinc-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-zinc-900">
                Lessons
              </CardTitle>
              <Button
                variant="gradient"
                size="sm"
                onClick={() => setAddLessonOpen(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Lesson
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {course.lessons?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-200 hover:bg-transparent">
                    <TableHead className="text-zinc-500 w-20">Pos</TableHead>
                    <TableHead className="text-zinc-500">Title</TableHead>
                    <TableHead className="text-zinc-500">Status</TableHead>
                    <TableHead className="text-zinc-500 hidden sm:table-cell">
                      Coach Only
                    </TableHead>
                    <TableHead className="text-zinc-500 text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {course.lessons.map((lesson) => (
                    <TableRow
                      key={lesson.id}
                      className="border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                      onClick={() =>
                        router.visit(
                          `/app/admin/courses/${course.id}/lessons/${lesson.id}`
                        )
                      }
                    >
                      <TableCell className="text-zinc-500">
                        <Input
                          type="number"
                          min="0"
                          defaultValue={lesson.position ?? ""}
                          className="w-16 h-8 text-center bg-white border-zinc-200 text-zinc-700"
                          onClick={(e) => e.stopPropagation()}
                          onBlur={(e) => {
                            const val = parseInt(e.target.value, 10)
                            if (!isNaN(val) && val !== lesson.position) handleReorder(lesson, val)
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.target.blur()
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell className="font-medium text-zinc-900">
                        {lesson.title}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={lesson.status} />
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {lesson.coachOnly ? (
                          <Badge
                            variant="outline"
                            className="border-amber-500 text-amber-600"
                          >
                            <EyeOff className="h-3 w-3 mr-1" />
                            Coach
                          </Badge>
                        ) : (
                          <span className="text-xs text-zinc-400">
                            <Eye className="h-3 w-3 inline mr-1" />
                            All
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation()
                              setDeleteLesson(lesson)
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-zinc-400">
                <BookOpen className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
                <p className="text-sm">No lessons yet</p>
                <p className="text-xs text-zinc-400 mt-1">
                  Add your first lesson to start building this course.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ---------------------------------------------------------------- */}
        {/* Section 3 — Group Assignment                                     */}
        {/* ---------------------------------------------------------------- */}
        <Card className="bg-white border-zinc-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-zinc-900">
                Group Assignment
              </CardTitle>
              <Button
                variant="gradient"
                size="sm"
                onClick={() => setAssignGroupOpen(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Assign Group
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {course.assignedGroups?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-200 hover:bg-transparent">
                    <TableHead className="text-zinc-500">Group</TableHead>
                    <TableHead className="text-zinc-500 text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {course.assignedGroups.map((group) => (
                    <TableRow
                      key={group.groupCourseId}
                      className="border-zinc-100 hover:bg-zinc-50"
                    >
                      <TableCell className="font-medium text-zinc-900">
                        {group.name}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setRemoveGroup(group)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Remove from course"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-zinc-400">
                <Users className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
                <p className="text-sm">No groups assigned</p>
                <p className="text-xs text-zinc-400 mt-1">
                  Assign groups to make this course available to their members.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ---------------------------------------------------------------- */}
        {/* Section 4 — Legacy course files (#594)                           */}
        {/* ---------------------------------------------------------------- */}
        {course.courseFiles?.length > 0 && (
          <Card className="bg-white border-zinc-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold text-zinc-900">
                Course Page Files
              </CardTitle>
              <p className="text-sm text-zinc-500">
                Old files from the previous platform. Members see them on the
                course page, above the lessons. To add or change a file, use a
                lesson&rsquo;s resources instead.
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableBody>
                  {course.courseFiles.map((file) => (
                    <TableRow
                      key={file.id}
                      className="border-zinc-100 hover:bg-zinc-50"
                    >
                      <TableCell className="font-medium text-zinc-900">
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 hover:underline"
                        >
                          <FileText className="h-4 w-4 text-zinc-400" />
                          {file.title}
                        </a>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setRemoveFile(file)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Remove from course page"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
        </>
        )}
      </div>

      {/* -------------------------------------------------------------------- */}
      {/* Dialogs                                                              */}
      {/* -------------------------------------------------------------------- */}

      {/* Edit Course */}
      <EditCourseDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        course={course}
        categories={categories}
      />

      {/* Delete Course */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="bg-white border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Course</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{course.title}&rdquo;? This
              will also remove all lessons, questions, and resources. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCourse}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Course
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Lesson */}
      <LessonFormDialog
        open={addLessonOpen}
        onOpenChange={setAddLessonOpen}
        courseId={course.id}
        nextPosition={
          (course.lessons?.reduce((max, l) => Math.max(max, l.position ?? 0), 0) ?? 0) + 1
        }
      />


      {/* Delete Lesson */}
      <AlertDialog
        open={!!deleteLesson}
        onOpenChange={(open) => !open && setDeleteLesson(null)}
      >
        <AlertDialogContent className="bg-white border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lesson</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{deleteLesson?.title}
              &rdquo;? This will also remove all questions and resources for this
              lesson. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLesson}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Lesson
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Assign Group */}
      <AssignGroupDialog
        open={assignGroupOpen}
        onOpenChange={setAssignGroupOpen}
        postUrl={`/app/admin/courses/${course.id}/group_assignments`}
        subject="course"
        availableGroups={availableGroups}
      />

      {/* Remove Group */}
      <AlertDialog
        open={!!removeGroup}
        onOpenChange={(open) => !open && setRemoveGroup(null)}
      >
        <AlertDialogContent className="bg-white border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Group</AlertDialogTitle>
            <AlertDialogDescription>
              Remove <strong>{removeGroup?.name}</strong> from this course?
              Members of this group will no longer have access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveGroup}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove legacy course file */}
      <AlertDialog
        open={!!removeFile}
        onOpenChange={(open) => !open && setRemoveFile(null)}
      >
        <AlertDialogContent className="bg-white border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove File</AlertDialogTitle>
            <AlertDialogDescription>
              Remove &ldquo;{removeFile?.title}&rdquo; from the course page?
              This cannot be undone. Files inside lessons are not affected.
              {removingLastContent &&
                " This is the course's only content, so members will no longer see the course."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveFile}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject with reason */}
      <ResponsiveDialog
        open={rejectOpen}
        onOpenChange={(open) => {
          if (!open) {
            setRejectionReason("")
            setRejectionError("")
          }
          setRejectOpen(open)
        }}
      >
        <ResponsiveDialogContent className="sm:max-w-md">
          <form onSubmit={handleRejectSubmit}>
            <ResponsiveDialogHeader>
              <ResponsiveDialogTitle className="font-display text-xl">
                Reject course
              </ResponsiveDialogTitle>
              <ResponsiveDialogDescription>
                The coach will see this reason on their course page so they can
                revise and resubmit.
              </ResponsiveDialogDescription>
            </ResponsiveDialogHeader>

            <div className="space-y-3 py-4 px-4 sm:px-0">
              <Label htmlFor="course-rejection-reason">
                Rejection reason <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="course-rejection-reason"
                value={rejectionReason}
                onChange={(e) => {
                  setRejectionReason(e.target.value)
                  if (rejectionError) setRejectionError("")
                }}
                placeholder="Explain what needs to change..."
                className="min-h-[120px] bg-white border-zinc-300 text-zinc-900"
                required
              />
              {rejectionError && (
                <p className="text-sm text-red-600">{rejectionError}</p>
              )}
            </div>

            <ResponsiveDialogFooter>
              <ResponsiveDialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </ResponsiveDialogClose>
              <Button
                type="submit"
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={rejecting || !rejectionReason.trim()}
              >
                {rejecting ? "Rejecting..." : "Reject course"}
              </Button>
            </ResponsiveDialogFooter>
          </form>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </AdminLayout>
  )
}
